import React, { useState } from 'react';
import axios from 'axios';
import * as Icons from 'lucide-react';
import { Mail, Phone, MapPin, Building2, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useReveal from '../hooks/useReveal';
import { CONTACT_INFO, SOCIALS } from '../mock/mock';
import { PhoneInput, COUNTRIES, validatePhone } from '../components/PhoneInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: '', email: '', company: '', phone: '', subject: '', message: '' };

const Contact = () => {
  const { t, lang } = useLang();
  useReveal(lang);
  const [form, setForm] = useState(initial);
  const [country, setCountry] = useState('GB');
  const [phoneError, setPhoneError] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const digits = form.phone.replace(/\D/g, '');
    if (digits && !validatePhone(digits)) { setPhoneError(true); return; }
    setPhoneError(false);
    const dial = (COUNTRIES.find((c) => c.iso === country) || {}).dial || '44';
    const fullPhone = digits ? `+${dial} ${digits}` : '';
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, { ...form, phone: fullPhone });
      if (window.gtag) window.gtag('event', 'ads_conversion_Contact_1', {});
      setStatus('success');
      setForm(initial);
    } catch (err) {
      setStatus('error');
    }
  };

  const field = 'w-full bg-transparent border-b border-espresso/20 py-3 text-espresso placeholder-mocha/50 focus:outline-none focus:border-gold transition-colors';

  return (
    <div>
      <section className="pt-40 pb-14 bg-gradient-to-b from-cream to-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold mb-4"><span className="w-8 h-px bg-gold" />{t('contact.kicker')}</span>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso">{t('contact.title')}</h1>
          <p className="mt-5 text-lg text-mocha max-w-xl mx-auto">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-14">
          {/* INFO */}
          <div className="lg:col-span-2 reveal">
            <h3 className="text-xs tracking-[0.22em] uppercase text-gold mb-8">{t('contact.infoTitle')}</h3>
            <ul className="space-y-7">
              <li className="flex gap-4"><Mail className="w-5 h-5 text-gold shrink-0 mt-1" /><div><p className="text-[0.68rem] tracking-[0.2em] uppercase text-mocha">{t('contact.emailLabel')}</p><p className="text-espresso mt-1">{CONTACT_INFO.displayEmail}</p></div></li>
              <li className="flex gap-4"><Phone className="w-5 h-5 text-gold shrink-0 mt-1" /><div><p className="text-[0.68rem] tracking-[0.2em] uppercase text-mocha">{t('contact.phoneLabel')}</p><p className="text-espresso mt-1">{CONTACT_INFO.phone}</p></div></li>
              <li className="flex gap-4"><MapPin className="w-5 h-5 text-gold shrink-0 mt-1" /><div><p className="text-[0.68rem] tracking-[0.2em] uppercase text-mocha">{t('contact.addressLabel')}</p><p className="text-espresso mt-1">{CONTACT_INFO.addressLine}</p></div></li>
              <li className="flex gap-4"><Building2 className="w-5 h-5 text-gold shrink-0 mt-1" /><div><p className="text-[0.68rem] tracking-[0.2em] uppercase text-mocha">HQ</p><p className="text-espresso mt-1">{CONTACT_INFO.hqLine}</p></div></li>
            </ul>

            <div className="mt-10 pt-8 border-t border-gold-soft">
              <h3 className="text-xs tracking-[0.22em] uppercase text-gold mb-5">{t('contact.followTitle')}</h3>
              <div className="space-y-3">
                {SOCIALS.map((s) => {
                  const Icon = Icons[s.icon] || Icons.Globe;
                  return (
                    <a key={s.name} href={s.url} target="_blank" rel="noreferrer" className="group flex items-center justify-between gap-4 px-4 py-3 bg-cream hover:bg-espresso transition-colors duration-400">
                      <span className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-gold group-hover:text-gold-light transition-colors" />
                        <span className="flex flex-col leading-tight">
                          <strong className="text-sm text-espresso group-hover:text-ivory transition-colors">{s.name}</strong>
                          <small className="text-xs text-mocha group-hover:text-ivory/70 transition-colors">{s.handle}</small>
                        </span>
                      </span>
                      <Icons.ArrowUpRight className="w-4 h-4 text-mocha group-hover:text-gold-light transition-colors" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-3 reveal">
            {status === 'success' ? (
              <div className="bg-cream border-t-2 border-gold p-12 text-center">
                <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-5" />
                <h3 className="font-serif text-3xl text-espresso">{t('contact.successTitle')}</h3>
                <p className="mt-3 text-mocha">{t('contact.successMsg')}</p>
                <button onClick={() => setStatus('idle')} className="mt-7 px-8 py-3 border border-espresso/30 text-espresso text-xs tracking-[0.18em] uppercase hover:border-gold hover:text-gold transition-colors">{t('contact.formSend')}</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="bg-cream p-8 md:p-10">
                <div className="grid sm:grid-cols-2 gap-6">
                  <input required name="name" value={form.name} onChange={onChange} placeholder={t('contact.formName')} className={field} />
                  <input required type="email" name="email" value={form.email} onChange={onChange} placeholder={t('contact.formEmail')} className={field} />
                  <input name="company" value={form.company} onChange={onChange} placeholder={t('contact.formCompany')} className={field} />
                  <div>
                    <PhoneInput
                      country={country}
                      onCountry={setCountry}
                      value={form.phone}
                      onChange={(v) => { setForm((f) => ({ ...f, phone: v })); setPhoneError(false); }}
                      placeholder={t('contact.formPhone')}
                      invalid={phoneError}
                    />
                    {phoneError && <p data-testid="phone-error-msg" className="mt-1.5 text-xs text-destructive">Please enter a valid phone number (6–14 digits).</p>}
                  </div>
                </div>
                <input name="subject" value={form.subject} onChange={onChange} placeholder={t('contact.formSubject')} className={`${field} mt-6`} />
                <textarea required name="message" value={form.message} onChange={onChange} placeholder={t('contact.formMessage')} rows={5} className={`${field} mt-6 resize-none`} />
                {status === 'error' && <p className="mt-4 text-sm text-destructive">{t('contact.errorMsg')}</p>}
                <button type="submit" disabled={status === 'sending'} className="group mt-8 inline-flex items-center gap-3 px-10 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors disabled:opacity-70">
                  {status === 'sending' ? <><Loader2 className="w-4 h-4 animate-spin" />{t('contact.formSending')}</> : <>{t('contact.formSend')}<Send className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
